import { CreditCardOutlined, NodeIndexOutlined, NumberOutlined, UserOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { CreditCardIcon, DeviceGroupsIcon, DevicesIcon, UserIcon } from 'assets/images/svg';
import dayjs from 'dayjs';
import { constants } from 'config/constants';
import { CREDENTIAL_TYPE_PASSCODE } from 'config/credentialTypes';
import { buildDeviceOptionsWithIntegration, buildGroupOptionsWithType } from 'library/helpers/integrationHelpers';
import { formatDisplayName } from 'library/helpers/utility';
import { useManagePermittedRoles } from 'library/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAssetGroups } from 'reduxStore/assetGroups/selectors';
import { selectCredentials, selectCredentialsByPersonID } from 'reduxStore/credentials/selectors';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { selectDevices } from 'reduxStore/devices/selectors';
import { selectPeople } from 'reduxStore/people/selectors';
import { selectPermissionsByPersonID } from 'reduxStore/permissions/selectors';
import ValidityPicker from 'zComponents/atoms/ValidityPicker';
import { Button, Col, Flex, Form, Modal, Row, Select, Steps, Tag, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const isZeroDate = (d) => !d || (typeof d === 'string' && d.startsWith('0001-01-01'));

const AddGuestModal = ({ children, visible: visibleProp, setVisible: setVisibleProp }) => {
	const {
		token: {
			colorTextLabel,
			fontWeightStrong,
			fontSizeLG,
			fontSizeSM,
			paddingXL,
			paddingSM,
			colorPrimary,
			marginXL,
			marginXS,
			colorBgLayout
		}
	} = theme.useToken();
	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const submitRef = useRef(false);

	// Redux
	const people = useSelector(selectPeople);
	const devices = useSelector(selectDevices);
	const deviceGroups = useSelector(selectAssetGroups);
	const allCredentials = useSelector(selectCredentials);
	const { loadingAddGuest } = useSelector((state) => state.Credentials);

	// Local State
	const [currentStep, setCurrentStep] = useState(0);
	const [internalVisible, setInternalVisible] = useState(false);

	// Step 1: Person
	const [selectedPersonID, setSelectedPersonID] = useState(null);

	// Step 2: Devices & Groups (tracks initial + current for diff)
	const [initialDevices, setInitialDevices] = useState([]);
	const [initialGroups, setInitialGroups] = useState([]);
	const [selectedDevices, setSelectedDevices] = useState([]);
	const [selectedDeviceGroups, setSelectedDeviceGroups] = useState([]);

	// Step 3: Credentials with individual dates
	// Map<credentialID, { startDate: dayjs|null, endDate: dayjs|null, isNew: bool, modified: bool }>
	const [selectedCredentials, setSelectedCredentials] = useState({});
	const [addCredentialID, setAddCredentialID] = useState(null);
	const [removedCredentialIDs, setRemovedCredentialIDs] = useState([]);

	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	// Permissions
	const canManagePermissions = useManagePermittedRoles('aCPermissionManagement');
	const canManageCredential = useManagePermittedRoles('updateCredential');
	const canAddGuest = canManagePermissions && canManageCredential;

	// Person's existing permissions
	const personPermissions = useSelector((state) => selectPermissionsByPersonID(state, selectedPersonID));
	const personCredentials = useSelector((state) => selectCredentialsByPersonID(state, selectedPersonID));

	// Pre-populate devices/groups when person is selected
	useEffect(() => {
		if (!selectedPersonID) {
			setInitialDevices([]);
			setInitialGroups([]);
			setSelectedDevices([]);
			setSelectedDeviceGroups([]);
			setSelectedCredentials({});
			return;
		}

		// Extract existing permissions — direct devices only (no assetGroupID means direct grant)
		const existingDeviceIDs = [
			...new Set(personPermissions.filter((p) => p.deviceID && !p.assetGroupID).map((p) => p.deviceID))
		];
		const existingGroupIDs = [
			...new Set(personPermissions.filter((p) => p.assetGroupID).map((p) => p.assetGroupID))
		];

		setInitialDevices(existingDeviceIDs);
		setInitialGroups(existingGroupIDs);
		setSelectedDevices(existingDeviceIDs);
		setSelectedDeviceGroups(existingGroupIDs);

		// Pre-populate existing credentials with their dates
		const credMap = {};
		personCredentials.forEach((c) => {
			const isPermanent = isZeroDate(c.startDate) || isZeroDate(c.endDate);
			credMap[c.credentialID] = {
				isPermanent,
				startDate: isPermanent ? null : dayjs(c.startDate),
				endDate: isPermanent ? null : dayjs(c.endDate),
				isNew: false,
				modified: false
			};
		});
		setSelectedCredentials(credMap);
	}, [selectedPersonID, personPermissions, personCredentials]);

	// Options
	const peopleOptions = people.map((p) => ({ label: formatDisplayName(p)?.displayName, value: p.personID }));
	const deviceOptions = buildDeviceOptionsWithIntegration(
		devices.filter((d) => !constants.GATEWAY_CATEGORIES.includes(d.defaultCategoryID))
	);
	const deviceGroupOptions = buildGroupOptionsWithType(deviceGroups);

	// Available credentials: unassigned ones (not already in selectedCredentials)
	const availableCredentialOptions = useMemo(
		() =>
			allCredentials
				.filter((c) => !selectedCredentials[c.credentialID])
				.map((c) => ({
					label: `${c.name}`,
					value: c.credentialID,
					searchLabel: `${c.name} ${c.value || ''}`
				}))
				.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
		[allCredentials, selectedCredentials]
	);

	// Handle adding a credential from dropdown
	const handleAddCredential = (credentialID) => {
		const start = dayjs();
		const end = start.add(1, 'week');
		setSelectedCredentials((prev) => ({
			...prev,
			[credentialID]: { isPermanent: false, startDate: start, endDate: end, isNew: true, modified: true }
		}));
		setAddCredentialID(null);
	};

	const handleRemoveCredential = (credentialID) => {
		setSelectedCredentials((prev) => {
			const entry = prev[credentialID];
			const next = { ...prev };
			delete next[credentialID];
			if (entry && !entry.isNew) {
				setRemovedCredentialIDs((r) => [...r, credentialID]);
			}
			return next;
		});
	};

	// Reset
	const resetForm = () => {
		setCurrentStep(0);
		form.resetFields();
		setSelectedPersonID(null);
		setInitialDevices([]);
		setInitialGroups([]);
		setSelectedDevices([]);
		setSelectedDeviceGroups([]);
		setSelectedCredentials({});
		setAddCredentialID(null);
		setRemovedCredentialIDs([]);
		submitRef.current = false;
	};

	const handleCancel = () => {
		if (loadingAddGuest) return;
		setVisible(false);
		resetForm();
	};

	const handleNext = () => {
		if (currentStep === 0 && !selectedPersonID) return;
		if (currentStep === 1 && selectedDevices.length === 0 && selectedDeviceGroups.length === 0) return;
		setCurrentStep((prev) => prev + 1);
	};

	const handleSubmit = () => {
		if (!selectedPersonID) return;

		// Compute permission diff
		const addedDevices = selectedDevices.filter((id) => !initialDevices.includes(id));
		const removedDevices = initialDevices.filter((id) => !selectedDevices.includes(id));
		const addedGroups = selectedDeviceGroups.filter((id) => !initialGroups.includes(id));
		const removedGroups = initialGroups.filter((id) => !selectedDeviceGroups.includes(id));

		// Compute credential changes
		const credentialUpdates = Object.entries(selectedCredentials)
			.filter(([, v]) => v.modified)
			.map(([id, v]) => ({
				credentialID: Number(id),
				isNew: v.isNew,
				startDate: v.startDate?.toISOString() || null,
				endDate: v.endDate?.toISOString() || null
			}));

		dispatch(
			credentialsActions.addGuest({
				personID: selectedPersonID,
				addedDevices,
				removedDevices,
				addedGroups,
				removedGroups,
				credentialUpdates,
				credentialUnassigns: removedCredentialIDs
			})
		);
		submitRef.current = true;
	};

	// Close on success
	useEffect(() => {
		if (!submitRef.current) return;
		if (!loadingAddGuest) {
			setVisible(false);
			resetForm();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loadingAddGuest]);

	// Steps
	const stepsData = [
		{ key: 0, label: 'Person', icon: <UserOutlined /> },
		{ key: 1, label: 'Devices & Groups', icon: <NodeIndexOutlined /> },
		{ key: 2, label: 'Credentials', icon: <CreditCardOutlined /> }
	];

	const stepsItems = stepsData.map((step) => ({
		title: (
			<Text
				style={{
					color: currentStep === step.key ? colorPrimary : undefined,
					fontSize: fontSizeLG,
					cursor: step.key <= currentStep ? 'pointer' : 'default'
				}}
				onClick={() => {
					if (step.key <= currentStep) setCurrentStep(step.key);
				}}
			>
				{step.label}
			</Text>
		),
		icon: step.icon,
		status: currentStep === step.key ? 'process' : currentStep > step.key ? 'finish' : 'wait',
		style: { height: 70 }
	}));

	const getTagColorAndIcon = (label) => {
		switch (label) {
			case 'Devices':
				return { color: 'gold', icon: <DevicesIcon style={{ marginRight: 4 }} color='currentColor' /> };
			case 'Device Groups':
				return { color: 'blue', icon: <DeviceGroupsIcon style={{ marginRight: 4 }} color='currentColor' /> };
			default:
				return { color: 'default', icon: null };
		}
	};

	const renderSelectWithTags = (label, placeholder, options, selected, setSelected) => {
		const { color, icon } = getTagColorAndIcon(label);
		return (
			<div>
				<Text style={{ fontWeight: fontWeightStrong }}>{label}</Text>
				<Select
					style={{ marginTop: 10, width: '100%' }}
					showSearch
					placeholder={placeholder}
					options={options}
					value={null}
					onSelect={(value) => {
						if (!selected.includes(value)) setSelected([...selected, value]);
					}}
					filterOption={(input, option) =>
						(option?.searchLabel || option?.label || '')
							.toString()
							.toLowerCase()
							.includes(input.toLowerCase())
					}
				/>
				<Flex wrap='wrap' gap='small' style={{ marginTop: 16 }}>
					{selected.map((id) => (
						<Tag
							key={id}
							color={color}
							closable
							style={{
								fontSize: fontSizeSM,
								fontWeight: fontWeightStrong,
								display: 'flex',
								alignItems: 'center',
								borderColor: 'currentcolor'
							}}
							onClose={() => setSelected(selected.filter((item) => item !== id))}
						>
							{icon}
							{options.find((opt) => opt.value === id)?.displayLabel ||
								options.find((opt) => opt.value === id)?.label}
						</Tag>
					))}
				</Flex>
			</div>
		);
	};

	const renderCredentialCard = (credentialID, entry) => {
		const cred = allCredentials.find((c) => c.credentialID === credentialID);
		if (!cred) return null;
		const isPasscode = cred.credentialTypeID === CREDENTIAL_TYPE_PASSCODE;

		return (
			<Flex
				key={credentialID}
				vertical
				gap={paddingSM}
				style={{
					background: colorBgLayout,
					padding: paddingSM,
					borderRadius: 8
				}}
			>
				<Flex justify='space-between' align='center'>
					<Tag
						color='magenta'
						style={{
							fontWeight: fontWeightStrong,
							borderColor: 'currentColor',
							marginInlineEnd: 0
						}}
					>
						<Flex align='center' gap={4}>
							{isPasscode ? <NumberOutlined /> : <CreditCardIcon color='currentColor' />}
							{cred.name} — {cred.value || 'No number'}
						</Flex>
					</Tag>
					<Flex align='center' gap={8}>
						{!entry.isNew && (
							<Text type='secondary' style={{ fontSize: 11 }}>
								Assigned
							</Text>
						)}
						<Button type='text' size='small' danger onClick={() => handleRemoveCredential(credentialID)}>
							{entry.isNew ? 'Remove' : 'Unassign'}
						</Button>
					</Flex>
				</Flex>

				<ValidityPicker
					isPermanent={entry.isPermanent}
					startDate={entry.startDate}
					endDate={entry.endDate}
					size='small'
					onChange={({ isPermanent: perm, startDate: s, endDate: e }) => {
						setSelectedCredentials((prev) => ({
							...prev,
							[credentialID]: {
								...prev[credentialID],
								isPermanent: perm,
								startDate: s,
								endDate: e,
								modified: true
							}
						}));
					}}
				/>
			</Flex>
		);
	};

	const isNextDisabled =
		(currentStep === 0 && !selectedPersonID) ||
		(currentStep === 1 && selectedDevices.length === 0 && selectedDeviceGroups.length === 0);

	const hasCredentialChanges = Object.values(selectedCredentials).some((v) => v.modified);
	const hasPermissionChanges =
		selectedDevices.some((id) => !initialDevices.includes(id)) ||
		initialDevices.some((id) => !selectedDevices.includes(id)) ||
		selectedDeviceGroups.some((id) => !initialGroups.includes(id)) ||
		initialGroups.some((id) => !selectedDeviceGroups.includes(id));

	const hasIncompleteDates = Object.values(selectedCredentials).some(
		(v) => !v.isPermanent && (!v.startDate || !v.endDate)
	);

	const isSubmitDisabled =
		hasIncompleteDates ||
		(!hasCredentialChanges && !hasPermissionChanges && removedCredentialIDs.length === 0);

	const selectedPerson = people.find((p) => p.personID === selectedPersonID);

	return (
		<>
			<Tooltip title={!canAddGuest ? "You don't have permission to perform this action." : 'Add Guest'}>
				<span onClick={() => canAddGuest && setVisible(true)}>
					{children ?? (
						<Button type='primary' disabled={!canAddGuest}>
							Add Guest
						</Button>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				closable={false}
				onCancel={handleCancel}
				width={930}
				centered
				destroyOnClose
				styles={{
					body: { minHeight: 500, maxHeight: '70vh', overflowY: 'auto' },
					content: { padding: paddingXL }
				}}
				footer={[
					<Flex justify='flex-end' gap={8} key='footer'>
						<Button onClick={handleCancel} disabled={loadingAddGuest}>
							Cancel
						</Button>
						{currentStep < stepsData.length - 1 ? (
							<Button type='primary' onClick={handleNext} disabled={isNextDisabled}>
								Next
							</Button>
						) : (
							<Button
								type='primary'
								onClick={handleSubmit}
								loading={loadingAddGuest}
								disabled={isSubmitDisabled}
							>
								Save
							</Button>
						)}
					</Flex>
				]}
			>
				<Flex vertical style={{ marginBottom: marginXL }}>
					<Title level={4} style={{ margin: 0, lineHeight: '28px' }}>
						Add Guest
					</Title>
					<Text style={{ color: colorTextLabel, lineHeight: '20px' }}>
						Select a person, manage their device access, and assign credentials with validity periods.
					</Text>
				</Flex>

				<Form form={form} layout='vertical'>
					<Row>
						<Col span={8}>
							<Steps
								direction='vertical'
								current={currentStep}
								style={{ height: 100 }}
								items={stepsItems}
							/>
						</Col>

						<Col span={16}>
							{/* Step 1: Select Person */}
							{currentStep === 0 && (
								<Flex vertical gap={16}>
									<Text style={{ fontWeight: fontWeightStrong }}>Person</Text>
									<Select
										style={{ width: '100%' }}
										showSearch
										placeholder='Search by name or email...'
										options={peopleOptions}
										value={selectedPersonID}
										onChange={(value) => {
											setSelectedPersonID(value);
											setCurrentStep(0);
										}}
										filterOption={(input, option) =>
											(option?.label || '').toLowerCase().includes(input.toLowerCase())
										}
										allowClear
									/>
									{selectedPerson && (
										<Tag
											color='purple'
											closable
											onClose={() => setSelectedPersonID(null)}
											style={{
												fontSize: fontSizeSM,
												fontWeight: fontWeightStrong,
												display: 'inline-flex',
												alignItems: 'center',
												borderColor: 'currentcolor',
												alignSelf: 'flex-start'
											}}
										>
											<UserIcon style={{ marginRight: 4 }} color='currentColor' />
											{formatDisplayName(selectedPerson)?.displayName}
										</Tag>
									)}
								</Flex>
							)}

							{/* Step 2: Devices & Groups */}
							{currentStep === 1 && (
								<Flex gap={26} vertical>
									{renderSelectWithTags(
										'Devices',
										'Enter device name',
										deviceOptions,
										selectedDevices,
										setSelectedDevices
									)}
									{renderSelectWithTags(
										'Device Groups',
										'Enter device group name',
										deviceGroupOptions,
										selectedDeviceGroups,
										setSelectedDeviceGroups
									)}
								</Flex>
							)}

							{/* Step 3: Credentials */}
							{currentStep === 2 && (
								<Flex vertical gap={16}>
									<Flex vertical gap={marginXS}>
										<Text style={{ fontWeight: fontWeightStrong }}>Add Credential</Text>
										<Select
											style={{ width: '100%' }}
											showSearch
											placeholder='Search unassigned credentials to add...'
											options={availableCredentialOptions}
											value={addCredentialID}
											onChange={handleAddCredential}
											filterOption={(input, option) =>
												(option?.searchLabel || '').toLowerCase().includes(input.toLowerCase())
											}
										/>
									</Flex>

									{Object.keys(selectedCredentials).length > 0 && (
										<Flex vertical gap={paddingSM}>
											<Text style={{ fontWeight: fontWeightStrong }}>
												Credentials ({Object.keys(selectedCredentials).length})
											</Text>
											{Object.entries(selectedCredentials).map(([id, entry]) =>
												renderCredentialCard(Number(id), entry)
											)}
										</Flex>
									)}

									{Object.keys(selectedCredentials).length === 0 && (
										<Text type='secondary' style={{ textAlign: 'center', marginTop: 16 }}>
											No credentials assigned. Use the dropdown above to add one.
										</Text>
									)}
								</Flex>
							)}
						</Col>
					</Row>
				</Form>
			</Modal>
		</>
	);
};

export default AddGuestModal;
